import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Privacy Policy | RESILIGENT Nigeria"
        description="RESILIGENT's privacy policy for Nigeria and Africa. How we collect, use, and protect your personal data in accordance with NDPR (Nigeria Data Protection Regulation), UK GDPR, and EU GDPR."
        keywords="privacy policy Nigeria, data protection Nigeria, NDPR compliance, RESILIGENT privacy"
        canonical="/privacy-policy"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-[#0a0a0a] pt-32 pb-16">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                Privacy Policy
              </h1>
              <p className="text-slate-400 text-base lg:text-lg leading-relaxed">
                Last updated: January 2025
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="bg-white py-16 lg:py-20">
          <div className="w-full max-w-4xl mx-auto px-6 lg:px-8">
            <div className="prose prose-slate max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Introduction
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  RESILIGENT ("we", "our", or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our services.
                </p>
                <p className="text-slate-600 text-base leading-relaxed">
                  We operate in accordance with applicable data protection laws, including the UK General Data Protection Regulation (UK GDPR), the EU General Data Protection Regulation (GDPR), and the Nigeria Data Protection Regulation (NDPR).
                </p>
              </div>

              {/* Information We Collect */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Information We Collect
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  We may collect the following types of information:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>Personal identification information:</strong> Name, email address, phone number, job title, and organisation name when you contact us or engage our services.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>Assessment and diagnostic data:</strong> Information collected through our Impact AS Plus™ platform and associated diagnostic tools, subject to separate data processing agreements.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>Technical data:</strong> IP address, browser type, device information, and browsing patterns when you visit our website.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>Communication data:</strong> Records of correspondence when you contact us.
                    </span>
                  </li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  How We Use Your Information
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Provide and deliver our advisory, coaching, and diagnostic services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Respond to enquiries and communicate with you about our services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Improve our website and service offerings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Comply with legal and regulatory obligations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Send relevant updates about our services (with your consent)</span>
                  </li>
                </ul>
              </div>

              {/* Cookies */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Cookies
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device that help us understand how you use our website.
                </p>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  We use:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>Essential cookies:</strong> Required for the website to function properly.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>Analytics cookies:</strong> Help us understand how visitors interact with our website.
                    </span>
                  </li>
                </ul>
                <p className="text-slate-600 text-base leading-relaxed">
                  You can manage your cookie preferences through your browser settings.
                </p>
              </div>

              {/* Data Sharing */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Data Sharing and Disclosure
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  We do not sell your personal data. We may share your information with:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Service providers who assist in delivering our services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Professional advisers and auditors where required</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Legal authorities when required by law</span>
                  </li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Your Rights
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal data:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Right to access your personal data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Right to rectification of inaccurate data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Right to erasure ("right to be forgotten")</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Right to restrict processing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Right to data portability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Right to object to processing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Right to withdraw consent</span>
                  </li>
                </ul>
                <p className="text-slate-600 text-base leading-relaxed">
                  To exercise any of these rights, please contact us using the details below.
                </p>
              </div>

              {/* Data Security */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Data Security
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. Our Impact AS Plus™ platform maintains enterprise-grade security standards.
                </p>
              </div>

              {/* Data Retention */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Data Retention
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, or as required by law. Assessment and diagnostic data retention periods are specified in our service agreements.
                </p>
              </div>

              {/* International Transfers */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  International Data Transfers
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  As we operate across multiple regions (West Africa, UK, Europe, Middle East), your data may be transferred internationally. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.
                </p>
              </div>

              {/* Contact */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Contact Us
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  If you have questions about this Privacy Policy or wish to exercise your data protection rights, please contact us:
                </p>
                <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>Email:</strong> info@resiligent.com
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>West Africa:</strong> 154 Prince Ade Odedina Street, Victoria Island, Lagos
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>Phone (Nigeria):</strong> +234 (0)703 009 0712
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>Europe:</strong> Monomark House, 27 Old Gloucester Street, WC1N 3AX, London UK
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed">
                    <strong>Phone (UK):</strong> +44 (0)207 117 630 7
                  </p>
                </div>
              </div>

              {/* Updates */}
              <div>
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  Changes to This Policy
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
